import { useEffect, useMemo, useRef, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { toast } from 'react-toastify'

import api from 'repositories/api'
import { IUserWorking } from 'store/modules/goal/types'
import { IStore } from 'store/modules/store/types'
import { distributeUserWeights } from 'utils/distributeUserWeights'
import { StoreCommissionValue } from '../components/BulkCommissionTable'
import { StoreRemunerationValue } from '../components/BulkRemunerationValuesTable'
import {
  fetchBulkLastActiveData,
  StoreLastActiveData
} from '../service'
import { BulkStore, StoreGoalConfig } from '../types'
import {
  buildEmptyStoreGoalConfig,
  hasValidUsersSelection,
  isStoreCommissionFilled,
  isStoreValuesFilled
} from '../utils'
import {
  applyCommissionFieldChange,
  applyCommissionData,
  applySellerGoalData,
  applyValueData,
  applyValueFieldChange,
  CommissionConfigField,
  CommissionConfigFieldValue,
  RemunerationConfigField
} from './storeGoalConfigUtils'

type StoreGoalData = StoreLastActiveData
type LoadDefaultsResult = boolean | null
type ConfigUpdater = (config: StoreGoalConfig) => StoreGoalConfig
type RemunerationDefaults = Pick<StoreGoalConfig, 'dsr' | 'inheritedSalaryComplement' | 'price' | 'salaryBase' | 'ticket' | 'total'>

type ConfigContextToken = {
  generation: number
  key: string
}

type LoadDefaultsOptions = {
  applyData: (config: StoreGoalConfig, selectedMonth: string, data: StoreGoalData) => StoreGoalConfig
  errorMessage: string
  isFilled: (config: StoreGoalConfig) => boolean
  onApplied?: (configs: StoreGoalConfig[], token: ConfigContextToken) => void
  setStatus: (status: 'applied' | 'skipped') => void
}

type ConfigContextSnapshot = {
  selectedMonth: string
  selectedStores: BulkStore[]
  storeIds: string[]
  token: ConfigContextToken
}

export type PreparedStoreGoalConfigs = {
  configs: StoreGoalConfig[]
  token: ConfigContextToken
}

const buildConfigContextKey = (
  selectedMonth: string,
  selectedStores: BulkStore[]
) => JSON.stringify([
  selectedMonth,
  selectedStores.map(store => store._id).sort()
])

const isSameConfigContext = (first: ConfigContextToken | null, second: ConfigContextToken) =>
  Boolean(first && first.generation === second.generation && first.key === second.key)

interface UseStoreGoalConfigsProps {
  selectedAvailableStores: BulkStore[]
  selectedMonth: string
}

export const useStoreGoalConfigs = ({
  selectedAvailableStores,
  selectedMonth
}: UseStoreGoalConfigsProps) => {
  const configContextKey = useMemo(
    () => buildConfigContextKey(selectedMonth, selectedAvailableStores),
    [selectedAvailableStores, selectedMonth]
  )
  const [storeGoalConfigs, setStoreGoalConfigs] = useState<StoreGoalConfig[]>([])
  const [commissionAutofillStatus, setCommissionAutofillStatus] = useState<'idle' | 'loading' | 'fetched' | 'applied' | 'skipped'>('idle')
  const [valuesAutofillStatus, setValuesAutofillStatus] = useState<'idle' | 'loading' | 'applied' | 'skipped'>('idle')
  const configContextRef = useRef<ConfigContextToken>({ generation: 0, key: configContextKey })
  const configLoadSequenceRef = useRef(0)
  const storeGoalConfigsRef = useRef<StoreGoalConfig[]>([])
  const sourceGoalDataByStoreIdRef = useRef<Map<string, StoreGoalData>>(new Map())
  const sourceGoalDataContextRef = useRef<ConfigContextToken | null>(null)
  const storeDetailsByStoreIdRef = useRef<Map<string, BulkStore>>(new Map())
  const storeDetailsContextRef = useRef<ConfigContextToken | null>(null)
  const valueDefaultsByStoreIdRef = useRef<Map<string, RemunerationDefaults>>(new Map())
  const valueDefaultsContextRef = useRef<ConfigContextToken | null>(null)

  if (configContextRef.current.key !== configContextKey) {
    configContextRef.current = {
      generation: configContextRef.current.generation + 1,
      key: configContextKey
    }
  }

  const isCurrentConfigContext = (token: ConfigContextToken) =>
    isSameConfigContext(configContextRef.current, token)

  const beginConfigLoadOperation = () => {
    configLoadSequenceRef.current += 1
    return configLoadSequenceRef.current
  }

  const canApplyConfigLoadResult = (token: ConfigContextToken, operationId: number) =>
    isCurrentConfigContext(token) && configLoadSequenceRef.current === operationId

  const createConfigContextSnapshot = (): ConfigContextSnapshot => ({
    selectedMonth,
    selectedStores: selectedAvailableStores,
    storeIds: selectedAvailableStores.map(store => store._id),
    token: configContextRef.current
  })

  useEffect(() => {
    storeGoalConfigsRef.current = storeGoalConfigs
  }, [storeGoalConfigs])

  const storeGoalConfigsByStoreId = useMemo(
    () => new Map(storeGoalConfigs.map(config => [config.storeId, config])),
    [storeGoalConfigs]
  )
  const commissionTableValues: StoreCommissionValue[] = storeGoalConfigs.map(config => ({
    directCommission: config.directCommission,
    distribute: config.distribute,
    periodType: config.periodType,
    splitCommission: config.splitCommission,
    storeId: config.storeId,
    storeName: config.storeName,
    storeSold: config.storeSold
  }))

  const remunerationTableValues: StoreRemunerationValue[] = storeGoalConfigs.map(config => ({
    dsr: config.dsr,
    price: config.price,
    salaryBase: config.salaryBase,
    storeId: config.storeId,
    storeName: config.storeName,
    ticket: config.ticket,
    total: config.total
  }))
  useEffect(() => {
    configLoadSequenceRef.current += 1
    sourceGoalDataByStoreIdRef.current = new Map()
    sourceGoalDataContextRef.current = null
    storeDetailsByStoreIdRef.current = new Map()
    storeDetailsContextRef.current = null
    valueDefaultsByStoreIdRef.current = new Map()
    valueDefaultsContextRef.current = null
    if (selectedAvailableStores.length === 0 || !selectedMonth) {
      storeGoalConfigsRef.current = []
      setStoreGoalConfigs([])
      setCommissionAutofillStatus('idle')
      setValuesAutofillStatus('idle')
      return
    }

    const emptyConfigs = selectedAvailableStores.map(store => buildEmptyStoreGoalConfig(store, selectedMonth))
    storeGoalConfigsRef.current = emptyConfigs
    setStoreGoalConfigs(emptyConfigs)
    setCommissionAutofillStatus('idle')
    setValuesAutofillStatus('idle')
  }, [configContextKey, selectedAvailableStores, selectedMonth])

  const loadSourceGoalData = async (snapshot: ConfigContextSnapshot, operationId: number) => {
    const hasLoadedAllStores =
      isSameConfigContext(sourceGoalDataContextRef.current, snapshot.token) &&
      snapshot.storeIds.length > 0 &&
      snapshot.storeIds.every(storeId => sourceGoalDataByStoreIdRef.current.has(storeId))
    if (hasLoadedAllStores) return sourceGoalDataByStoreIdRef.current

    const bulkGoalData = await fetchBulkLastActiveData(snapshot.storeIds)

    if (!canApplyConfigLoadResult(snapshot.token, operationId)) return null

    const nextSourceGoalDataByStoreId = new Map(bulkGoalData.map(data => [data.storeId, data as StoreGoalData]))
    sourceGoalDataByStoreIdRef.current = nextSourceGoalDataByStoreId
    sourceGoalDataContextRef.current = snapshot.token
    return nextSourceGoalDataByStoreId
  }

  const loadStoreDetails = async (snapshot: ConfigContextSnapshot, operationId: number) => {
    const hasLoadedAllStores =
      isSameConfigContext(storeDetailsContextRef.current, snapshot.token) &&
      snapshot.storeIds.length > 0 &&
      snapshot.storeIds.every(storeId => storeDetailsByStoreIdRef.current.has(storeId))
    if (hasLoadedAllStores) return storeDetailsByStoreIdRef.current

    const { data } = await api.axios.post<IStore[]>('/store/bulk-goal-details', { storeIds: snapshot.storeIds })

    if (!canApplyConfigLoadResult(snapshot.token, operationId)) return null

    const nextStoreDetailsByStoreId = new Map(data.map(store => [store._id, store as BulkStore]))
    storeDetailsByStoreIdRef.current = nextStoreDetailsByStoreId
    storeDetailsContextRef.current = snapshot.token
    return nextStoreDetailsByStoreId
  }

  const updateAllConfigs = (updater: ConfigUpdater) => {
    setStoreGoalConfigs(configs => configs.map(updater))
  }

  const updateConfig = (storeId: string, updater: ConfigUpdater) => {
    updateAllConfigs(config => config.storeId === storeId ? updater(config) : config)
  }

  const handleCommissionTableChange = (storeId: string, field: CommissionConfigField, value: CommissionConfigFieldValue) => {
    updateConfig(storeId, config => applyCommissionFieldChange(config, selectedMonth, field, value))
  }

  const handleCommissionApplyToAll = (field: CommissionConfigField, value: CommissionConfigFieldValue) => {
    updateAllConfigs(config => field === 'splitCommission' && config.periodType === 'single'
      ? config
      : applyCommissionFieldChange(config, selectedMonth, field, value)
    )
  }

  const handleValuesTableChange = (storeId: string, field: RemunerationConfigField, value: number | null) => {
    updateConfig(storeId, config => applyValueFieldChange(config, field, value))
  }

  const handleValuesApplyToAll = (field: RemunerationConfigField, value: number) => {
    updateAllConfigs(config => applyValueFieldChange(config, field, value))
  }

  const getConfigsWithSourceData = (
    configs: StoreGoalConfig[],
    snapshot: ConfigContextSnapshot,
    sourceGoalData: Map<string, StoreGoalData>,
    updater: (config: StoreGoalConfig, data: StoreGoalData) => StoreGoalConfig
  ) => snapshot.selectedStores.map(store => {
    const config = configs.find(currentConfig => currentConfig.storeId === store._id) || buildEmptyStoreGoalConfig(store, snapshot.selectedMonth)
    const data = sourceGoalData.get(store._id)
    return data ? updater(config, data) : config
  })

  const loadDefaults = async ({
    applyData,
    errorMessage,
    isFilled,
    onApplied,
    setStatus
  }: LoadDefaultsOptions): Promise<LoadDefaultsResult> => {
    const snapshot = createConfigContextSnapshot()
    const operationId = beginConfigLoadOperation()

    if (snapshot.selectedStores.length === 0 || !snapshot.selectedMonth) return false

    try {
      const sourceGoalData = await loadSourceGoalData(snapshot, operationId)
      if (!sourceGoalData || !canApplyConfigLoadResult(snapshot.token, operationId)) return null

      const nextConfigs = getConfigsWithSourceData(
        storeGoalConfigsRef.current,
        snapshot,
        sourceGoalData,
        (config, data) => applyData(config, snapshot.selectedMonth, data)
      )
      if (!canApplyConfigLoadResult(snapshot.token, operationId)) return null

      onApplied?.(nextConfigs, snapshot.token)
      storeGoalConfigsRef.current = nextConfigs
      unstable_batchedUpdates(() => {
        setStoreGoalConfigs(nextConfigs)
        setStatus('applied')
      })
      return nextConfigs.length > 0 && nextConfigs.every(isFilled)
    } catch {
      if (!canApplyConfigLoadResult(snapshot.token, operationId)) return null

      setStatus('skipped')
      toast.error(errorMessage)
      return null
    }
  }

  const loadCommissionDefaults = () => loadDefaults({
    applyData: applyCommissionData,
    errorMessage: 'Não foi possível carregar as configurações anteriores das lojas. Tente novamente.',
    isFilled: isStoreCommissionFilled,
    setStatus: setCommissionAutofillStatus
  })

  const loadValueDefaults = () => loadDefaults({
    applyData: applyValueData,
    errorMessage: 'Não foi possível carregar os valores anteriores das lojas. Tente novamente.',
    isFilled: isStoreValuesFilled,
    onApplied: (configs, token) => {
      valueDefaultsByStoreIdRef.current = new Map(configs.map(config => [config.storeId, {
        dsr: config.dsr,
        price: config.price,
        salaryBase: config.salaryBase,
        inheritedSalaryComplement: config.inheritedSalaryComplement,
        ticket: config.ticket,
        total: config.total
      }]))
      valueDefaultsContextRef.current = token
    },
    setStatus: setValuesAutofillStatus
  })

  const resetValueDefaults = () => {
    if (!isSameConfigContext(valueDefaultsContextRef.current, configContextRef.current)) return
    const nextConfigs = storeGoalConfigsRef.current.map(config => {
      const defaults = valueDefaultsByStoreIdRef.current.get(config.storeId)
      if (!defaults) return config
      return {
        ...config,
        ...defaults,
        monthGoalTemplates: [],
        sellerGoalDataLoaded: false
      }
    })
    storeGoalConfigsRef.current = nextConfigs
    setStoreGoalConfigs(nextConfigs)
  }

  const prepareSellerGoalConfigs = async () => {
    const snapshot = createConfigContextSnapshot()
    const operationId = beginConfigLoadOperation()

    if (snapshot.selectedStores.length === 0 || !snapshot.selectedMonth) return null

    try {
      const [sourceGoalData, storeDetails] = await Promise.all([
        loadSourceGoalData(snapshot, operationId),
        loadStoreDetails(snapshot, operationId)
      ])

      if (!sourceGoalData || !storeDetails || !canApplyConfigLoadResult(snapshot.token, operationId)) return null

      const configsByStoreId = new Map(storeGoalConfigsRef.current.map(config => [config.storeId, config]))
      const nextConfigs = snapshot.selectedStores.map(store => {
        const config = configsByStoreId.get(store._id) || buildEmptyStoreGoalConfig(store, snapshot.selectedMonth)
        if (config.sellerGoalDataLoaded) return config
        const fullStore = storeDetails.get(store._id) || store
        return applySellerGoalData(config, fullStore, snapshot.selectedMonth, sourceGoalData.get(store._id))
      })
      if (!canApplyConfigLoadResult(snapshot.token, operationId)) return null

      return {
        configs: nextConfigs,
        token: snapshot.token
      }
    } catch {
      if (!canApplyConfigLoadResult(snapshot.token, operationId)) return null

      toast.error('Não foi possível carregar os dados dos vendedores das lojas. Tente novamente.')
      return null
    }
  }

  const commitStoreGoalConfigs = (preparedConfigs: PreparedStoreGoalConfigs) => {
    if (!isCurrentConfigContext(preparedConfigs.token)) return false

    storeGoalConfigsRef.current = preparedConfigs.configs
    unstable_batchedUpdates(() => {
      setStoreGoalConfigs(preparedConfigs.configs)
    })
    return true
  }

  const updateStoreUsersSelection = (
    storeId: string,
    updater: (config: StoreGoalConfig) => Pick<StoreGoalConfig, 'usersNotWorking' | 'usersWorking'>
  ) => {
    updateConfig(storeId, config => {
      const nextUsers = updater(config)
      return { ...config, ...nextUsers }
    })
  }

  const addSellerToGoal = (storeId: string, user: IUserWorking) => {
    updateStoreUsersSelection(storeId, config => {
      const usersWorking = distributeUserWeights(
        [...config.usersWorking, { ...user, active: true }],
        config.distribute
      )

      return {
        usersNotWorking: config.usersNotWorking.filter(currentUser => currentUser.userId?._id !== user.userId?._id),
        usersWorking
      }
    })
  }

  const removeSellerFromGoal = (storeId: string, user: IUserWorking) => {
    updateStoreUsersSelection(storeId, config => {
      const usersWorking = distributeUserWeights(
        config.usersWorking.filter(currentUser => currentUser.userId?._id !== user.userId?._id),
        config.distribute
      )

      return {
        usersNotWorking: [...config.usersNotWorking, { ...user, active: false, weightLocked: false }],
        usersWorking
      }
    })
  }

  const updateSellerDsr = (storeId: string, user: IUserWorking, dsr: boolean) => {
    updateStoreUsersSelection(storeId, config => ({
      usersNotWorking: config.usersNotWorking,
      usersWorking: config.usersWorking.map(currentUser => currentUser.userId?._id === user.userId?._id ? { ...currentUser, dsr } : currentUser)
    }))
  }

  const updateSellerWeight = (storeId: string, user: IUserWorking, type: 'auto' | 'fixed', weight: number) => {
    updateStoreUsersSelection(storeId, config => {
      const updatedUsers = config.usersWorking.map(currentUser => currentUser.userId?._id === user.userId?._id
        ? { ...currentUser, goalWeight: +weight, weightLocked: type === 'fixed' }
        : currentUser
      )

      return {
        usersNotWorking: config.usersNotWorking,
        usersWorking: distributeUserWeights(updatedUsers, config.distribute)
      }
    })
  }

  const unlockSellerWeights = (storeId: string) => {
    updateStoreUsersSelection(storeId, config => ({
      usersNotWorking: config.usersNotWorking,
      usersWorking: config.usersWorking.map(user => ({ ...user, goalWeight: 1, weightLocked: false }))
    }))
  }

  return {
    addSellerToGoal,
    commissionAutofillStatus,
    commissionTableValues,
    handleCommissionApplyToAll,
    handleCommissionTableChange,
    handleValuesApplyToAll,
    handleValuesTableChange,
    hasValidUsersSelection,
    loadCommissionDefaults,
    loadValueDefaults,
    prepareSellerGoalConfigs,
    resetValueDefaults,
    commitStoreGoalConfigs,
    remunerationTableValues,
    removeSellerFromGoal,
    storeGoalConfigs,
    storeGoalConfigsByStoreId,
    unlockSellerWeights,
    updateSellerDsr,
    updateSellerWeight,
    valuesAutofillStatus
  }
}
